import React, { useState, useEffect, useMemo } from 'react';
import styles from './styles/StepReferralSource.module.css';
import sharedStyles from './styles/BrandSignup.module.css';
import { Luna } from './Luna';

const StepReferralSource = ({ brandSignup, onComplete, onSkipToDashboard, loading, error, onBack, canGoBack }) => {
  const [referralSource, setReferralSource] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    // Pre-fill if we have step data
    const stepData = brandSignup.step_data?.['referral_source'];
    if (stepData) {
      setReferralSource(stepData.referral_source || '');
    }
  }, [brandSignup.step_data]);

  const referralOptions = [
    { value: 'friend_or_colleague', label: 'Friend or colleague', icon: '👥' },
    { value: 'google_search', label: 'Google search', icon: '🔍' },
    { value: 'instagram', label: 'Instagram', icon: '📸' },
    { value: 'tiktok', label: 'TikTok', icon: '📱' },
    { value: 'linkedin', label: 'LinkedIn', icon: '💼' },
    { value: 'brand_or_factory', label: 'Brand or factory', icon: '🏭' },
    { value: 'event_or_conference', label: 'Event or conference', icon: '🎫' },
    { value: 'trade_organization', label: 'Trade organization', icon: '🏢' },
    { value: 'news_article', label: 'News article', icon: '📰' },
    { value: 'other', label: 'Other', icon: '📝' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched(true);

    if (!referralSource) {
      return;
    }

    onComplete({
      referral_source: referralSource
    });
  };

  const handleChange = (e) => {
    setReferralSource(e.target.value);
    if (touched && e.target.value) {
      setTouched(false);
    }
  };

  // Memoize Luna messages and component to prevent re-typing on form changes
  const lunaMessages = useMemo(() => [
    "Almost there! I'm curious - how did you find us?",
    "This helps us know where to find more amazing brands like yours!"
  ], []);

  const memoizedLuna = useMemo(() =>
    <Luna lunaDialogs={lunaMessages} />,
    [lunaMessages]
  );

  return (
    <div className={styles.stepContainer}>
      {memoizedLuna}

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.formField}>
          <label htmlFor="referralSource" className={styles.label}>
            How did you hear about us?
          </label>

          <div className={styles.selectWrapper}>
            <select
              id="referralSource"
              value={referralSource}
              onChange={handleChange}
              className={styles.select}
              disabled={loading}
            >
              <option value="">Select an option...</option>
              {referralOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.icon} {option.label}
                </option>
              ))}
            </select>
          </div>

          {touched && !referralSource && (
            <p className={styles.errorMessage}>Please select how you heard about us</p>
          )}
        </div>

        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}

        <button
          className={styles.button}
          type="submit"
          disabled={loading || !referralSource}
        >
          {loading ? 'Processing...' : 'Continue'}
        </button>

        {canGoBack && (
          <a
            className={sharedStyles.backLink}
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
          >
            ← Back
          </a>
        )}

        {onSkipToDashboard && (
          <button
            type="button"
            className={sharedStyles.skipLink}
            onClick={onSkipToDashboard}
            disabled={loading}
          >
            I'll set up my project later. Take me to the Dashboard
          </button>
        )}
      </form>
    </div>
  );
};

export default StepReferralSource;